import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Mail, Phone, MapPin } from 'lucide-react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
type LegalSection = {
  title: string;
  content: string | string[];
};
type LegalPageProps = {
  title: string;
  lastUpdated?: string;
  sections: LegalSection[];
  showContactInfo?: boolean;
};
export function LegalPage({
  title,
  lastUpdated,
  sections,
  showContactInfo = true
}: LegalPageProps) {
  return <div className="min-h-screen bg-[#0a1628] text-white" data-id="element-597">
      <Navigation data-id="element-598" />

      <main className="pt-32 pb-24" data-id="element-599">
        <div className="max-w-4xl mx-auto px-6 lg:px-8" data-id="element-600">
          {/* Back Button */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} className="mb-8" data-id="element-601">
            <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-[#d4a853] transition-colors text-sm font-medium uppercase tracking-wider" data-id="element-602">
              <ArrowLeft size={16} data-id="element-603" />
              Back to Home
            </Link>
          </motion.div>

          {/* Header */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="mb-12" data-id="element-604">
            <div className="flex items-center gap-3 mb-6" data-id="element-605">
              <div className="h-px w-12 bg-[#d4a853]" data-id="element-606" />
              <span className="text-[#d4a853] font-medium tracking-[0.2em] uppercase text-sm" data-id="element-607">
                Legal
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-608">
              {title}
            </h1>
            {lastUpdated && <p className="text-gray-400 text-sm" data-id="element-609">
                Last Updated: {lastUpdated}
              </p>}
          </motion.div>

          {/* Content Sections */}
          <div className="space-y-12" data-id="element-610">
            {sections.map((section, index) => <motion.section key={index} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2 + index * 0.1
          }} className="bg-[#1a2d4a]/30 border border-white/10 p-8" data-id="element-611">
                <h2 className="text-2xl font-bold text-[#d4a853] mb-4" data-id="element-612">
                  {section.title}
                </h2>
                {Array.isArray(section.content) ? <div className="space-y-4" data-id="element-613">
                    {section.content.map((paragraph, i) => <p key={i} className="text-gray-300 leading-relaxed text-base" data-id="element-614">
                        {paragraph}
                      </p>)}
                  </div> : <p className="text-gray-300 leading-relaxed text-base" data-id="element-615">
                    {section.content}
                  </p>}
              </motion.section>)}
          </div>

          {/* Contact Information */}
          {showContactInfo && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.5
        }} className="mt-16 bg-[#1a2d4a] border border-[#d4a853]/30 p-8" data-id="element-616">
              <h2 className="text-2xl font-bold text-white mb-6" data-id="element-617">
                Contact Information
              </h2>
              <p className="text-gray-400 mb-6" data-id="element-618">
                For legal inquiries, please contact:
              </p>

              <div className="space-y-4" data-id="element-619">
                <div className="flex items-start gap-4" data-id="element-620">
                  <div className="w-10 h-10 bg-[#0a1628] flex items-center justify-center flex-shrink-0" data-id="element-621">
                    <MapPin className="text-[#d4a853] w-5 h-5" data-id="element-622" />
                  </div>
                  <div data-id="element-623">
                    <p className="text-white font-medium mb-1" data-id="element-624">Address</p>
                    <p className="text-gray-400 text-sm leading-relaxed" data-id="element-625">
                      Vermeulens Garden Centre
                      <br data-id="element-626" />
                      Horton Road, Staines-Upon-Thames
                      <br data-id="element-627" />
                      Middlesex, TW19 6AE
                      <br data-id="element-628" />
                      United Kingdom
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-629">
                  <div className="w-10 h-10 bg-[#0a1628] flex items-center justify-center flex-shrink-0" data-id="element-630">
                    <Mail className="text-[#d4a853] w-5 h-5" data-id="element-631" />
                  </div>
                  <div data-id="element-632">
                    <p className="text-white font-medium mb-1" data-id="element-633">Email</p>
                    <a href="mailto:consult@888sourcingltd.co.uk" className="text-[#d4a853] hover:text-white transition-colors text-sm" data-id="element-634">
                      consult@888sourcingltd.co.uk
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-635">
                  <div className="w-10 h-10 bg-[#0a1628] flex items-center justify-center flex-shrink-0" data-id="element-636">
                    <Phone className="text-[#d4a853] w-5 h-5" data-id="element-637" />
                  </div>
                  <div data-id="element-638">
                    <p className="text-white font-medium mb-1" data-id="element-639">Phone</p>
                    <a href="tel:+441784693046" className="text-[#d4a853] hover:text-white transition-colors text-sm" data-id="element-640">
                      +44 (0) 1784 693046
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>}

          {/* Back to Top */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.6
        }} className="mt-12 text-center" data-id="element-641">
            <Link to="/" className="inline-flex items-center gap-2 text-[#d4a853] hover:text-white transition-colors font-bold uppercase tracking-wider text-sm" data-id="element-642">
              <ArrowLeft size={16} data-id="element-643" />
              Return to Homepage
            </Link>
          </motion.div>
        </div>
      </main>

      <Footer data-id="element-644" />
    </div>;
}